"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.all = all;
exports.touchOnly = exports.targetNotEditable = exports.singleClick = exports.shiftKeyOnly = exports.primaryAction = exports.pointerMove = exports.platformModifierKeyOnly = exports.penOnly = exports.noModifierKeys = exports.never = exports.mouseOnly = exports.mouseActionButton = exports.focusWithTabindex = exports.focus = exports.doubleClick = exports.click = exports.always = exports.altShiftKeysOnly = exports.altKeyOnly = void 0;

var _MapBrowserEventType = _interopRequireDefault(require("../MapBrowserEventType.js"));

var _functions = require("../functions.js");

var _has = require("../has.js");

var _asserts = require("../asserts.js");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

/**
 * @module ol/events/condition
 */

/**
 * A function that takes an {@link module:ol/MapBrowserEvent~MapBrowserEvent} and returns a
 * `{boolean}`. If the condition is met, true should be returned.
 *
 * @typedef {function(this: ?, import("../MapBrowserEvent.js").default): boolean} Condition
 */

/**
 * Creates a condition function that passes when all provided conditions pass.
 * @param {...Condition} var_args Conditions to check.
 * @return {Condition} Condition function.
 */
function all(var_args) {
  var conditions = arguments;
  /**
   * @param {import("../MapBrowserEvent.js").default} event Event.
   * @return {boolean} All conditions passed.
   */

  return function (event) {
    var pass = true;

    for (var i = 0, ii = conditions.length; i < ii; ++i) {
      pass = pass && conditions[i](event);

      if (!pass) {
        break;
      }
    }

    return pass;
  };
}
/**
 * Return `true` if only the alt-key is pressed, `false` otherwise (e.g. when
 * additionally the shift-key is pressed).
 *
 * @param {import("../MapBrowserEvent.js").default} mapBrowserEvent Map browser event.
 * @return {boolean} True if only the alt key is pressed.
 * @api
 */


var altKeyOnly = function altKeyOnly(mapBrowserEvent) {
  var originalEvent =
  /** @type {KeyboardEvent|MouseEvent|TouchEvent} */
  mapBrowserEvent.originalEvent;
  return originalEvent.altKey && !(originalEvent.metaKey || originalEvent.ctrlKey) && !originalEvent.shiftKey;
};
/**
 * Return `true` if only the alt-key and shift-key is pressed, `false` otherwise
 * (e.g. when additionally the platform-modifier-key is pressed).
 *
 * @param {import("../MapBrowserEvent.js").default} mapBrowserEvent Map browser event.
 * @return {boolean} True if only the alt and shift keys are pressed.
 * @api
 */


exports.altKeyOnly = altKeyOnly;

var altShiftKeysOnly = function altShiftKeysOnly(mapBrowserEvent) {
  var originalEvent =
  /** @type {KeyboardEvent|MouseEvent|TouchEvent} */
  mapBrowserEvent.originalEvent;
  return originalEvent.altKey && !(originalEvent.metaKey || originalEvent.ctrlKey) && originalEvent.shiftKey;
};
/**
 * Return `true` if the map has the focus. This condition requires a map target
 * element with a `tabindex` attribute, e.g. `<div id="map" tabindex="1">`.
 *
 * @param {import("../MapBrowserEvent.js").default} event Map browser event.
 * @return {boolean} The map has the focus.
 * @api
 */


exports.altShiftKeysOnly = altShiftKeysOnly;

var focus = function focus(event) {
  var targetElement = event.map.getTargetElement();
  var activeElement = event.map.getOwnerDocument().activeElement;
  return targetElement.contains(activeElement);
};
/**
 * Return `true` if the map has the focus or no 'tabindex' attribute set.
 *
 * @param {import("../MapBrowserEvent.js").default} event Map browser event.
 * @return {boolean} The map container has the focus or no 'tabindex' attribute.
 */


exports.focus = focus;

var focusWithTabindex = function focusWithTabindex(event) {
  return event.map.getTargetElement().hasAttribute('tabindex') ? focus(event) : true;
};
/**
 * Return always true.
 *
 * @param {import("../MapBrowserEvent.js").default} mapBrowserEvent Map browser event.
 * @return {boolean} True.
 * @api
 */


exports.focusWithTabindex = focusWithTabindex;
var always = _functions.TRUE;
/**
 * Return `true` if the event is a `click` event, `false` otherwise.
 *
 * @param {import("../MapBrowserEvent.js").default} mapBrowserEvent Map browser event.
 * @return {boolean} True if the event is a map `click` event.
 * @api
 */

exports.always = always;

var click = function click(mapBrowserEvent) {
  return mapBrowserEvent.type == _MapBrowserEventType["default"].CLICK;
};
/**
 * Return `true` if the event has an "action"-producing mouse button.
 *
 * By definition, this includes left-click on windows/linux, and left-click
 * without the ctrl key on Macs.
 *
 * @param {import("../MapBrowserEvent.js").default} mapBrowserEvent Map browser event.
 * @return {boolean} The result.
 */


exports.click = click;

var mouseActionButton = function mouseActionButton(mapBrowserEvent) {
  var originalEvent =
  /** @type {MouseEvent} */
  mapBrowserEvent.originalEvent;
  return originalEvent.button == 0 && !(_has.WEBKIT && _has.MAC && originalEvent.ctrlKey);
};
/**
 * Return always false.
 *
 * @param {import("../MapBrowserEvent.js").default} mapBrowserEvent Map browser event.
 * @return {boolean} False.
 * @api
 */


exports.mouseActionButton = mouseActionButton;
var never = _functions.FALSE;
/**
 * Return `true` if the browser event is a `pointermove` event, `false`
 * otherwise.
 *
 * @param {import("../MapBrowserEvent.js").default} mapBrowserEvent Map browser event.
 * @return {boolean} True if the browser event is a `pointermove` event.
 * @api
 */

exports.never = never;

var pointerMove = function pointerMove(mapBrowserEvent) {
  return mapBrowserEvent.type == 'pointermove';
};
/**
 * Return `true` if the event is a map `singleclick` event, `false` otherwise.
 *
 * @param {import("../MapBrowserEvent.js").default} mapBrowserEvent Map browser event.
 * @return {boolean} True if the event is a map `singleclick` event.
 * @api
 */


exports.pointerMove = pointerMove;

var singleClick = function singleClick(mapBrowserEvent) {
  return mapBrowserEvent.type == _MapBrowserEventType["default"].SINGLECLICK;
};
/**
 * Return `true` if the event is a map `dblclick` event, `false` otherwise.
 *
 * @param {import("../MapBrowserEvent.js").default} mapBrowserEvent Map browser event.
 * @return {boolean} True if the event is a map `dblclick` event.
 * @api
 */


exports.singleClick = singleClick;

var doubleClick = function doubleClick(mapBrowserEvent) {
  return mapBrowserEvent.type == _MapBrowserEventType["default"].DBLCLICK;
};
/**
 * Return `true` if no modifier key (alt-, shift- or platform-modifier-key) is
 * pressed.
 *
 * @param {import("../MapBrowserEvent.js").default} mapBrowserEvent Map browser event.
 * @return {boolean} True only if there no modifier keys are pressed.
 * @api
 */


exports.doubleClick = doubleClick;

var noModifierKeys = function noModifierKeys(mapBrowserEvent) {
  var originalEvent =
  /** @type {KeyboardEvent|MouseEvent|TouchEvent} */
  mapBrowserEvent.originalEvent;
  return !originalEvent.altKey && !(originalEvent.metaKey || originalEvent.ctrlKey) && !originalEvent.shiftKey;
};
/**
 * Return `true` if only the platform-modifier-key (the meta-key on Mac,
 * ctrl-key otherwise) is pressed, `false` otherwise (e.g. when additionally
 * the shift-key is pressed).
 *
 * @param {import("../MapBrowserEvent.js").default} mapBrowserEvent Map browser event.
 * @return {boolean} True if only the platform modifier key is pressed.
 * @api
 */


exports.noModifierKeys = noModifierKeys;

var platformModifierKeyOnly = function platformModifierKeyOnly(mapBrowserEvent) {
  var originalEvent =
  /** @type {KeyboardEvent|MouseEvent|TouchEvent} */
  mapBrowserEvent.originalEvent;
  return !originalEvent.altKey && (_has.MAC ? originalEvent.metaKey : originalEvent.ctrlKey) && !originalEvent.shiftKey;
};
/**
 * Return `true` if only the shift-key is pressed, `false` otherwise (e.g. when
 * additionally the alt-key is pressed).
 *
 * @param {import("../MapBrowserEvent.js").default} mapBrowserEvent Map browser event.
 * @return {boolean} True if only the shift key is pressed.
 * @api
 */


exports.platformModifierKeyOnly = platformModifierKeyOnly;

var shiftKeyOnly = function shiftKeyOnly(mapBrowserEvent) {
  var originalEvent =
  /** @type {KeyboardEvent|MouseEvent|TouchEvent} */
  mapBrowserEvent.originalEvent;
  return !originalEvent.altKey && !(originalEvent.metaKey || originalEvent.ctrlKey) && originalEvent.shiftKey;
};
/**
 * Return `true` if the target element is not editable, i.e. not a `<input>`-,
 * `<select>`- or `<textarea>`-element, `false` otherwise.
 *
 * @param {import("../MapBrowserEvent.js").default} mapBrowserEvent Map browser event.
 * @return {boolean} True only if the target element is not editable.
 * @api
 */


exports.shiftKeyOnly = shiftKeyOnly;

var targetNotEditable = function targetNotEditable(mapBrowserEvent) {
  var originalEvent =
  /** @type {KeyboardEvent|MouseEvent|TouchEvent} */
  mapBrowserEvent.originalEvent;
  var tagName =
  /** @type {Element} */
  originalEvent.target.tagName;
  return tagName !== 'INPUT' && tagName !== 'SELECT' && tagName !== 'TEXTAREA';
};
/**
 * Return `true` if the event originates from a mouse device.
 *
 * @param {import("../MapBrowserEvent.js").default} mapBrowserEvent Map browser event.
 * @return {boolean} True if the event originates from a mouse device.
 * @api
 */


exports.targetNotEditable = targetNotEditable;

var mouseOnly = function mouseOnly(mapBrowserEvent) {
  var pointerEvent =
  /** @type {import("../MapBrowserEvent").default} */
  mapBrowserEvent.originalEvent;
  (0, _asserts.assert)(pointerEvent !== undefined, 56); // mapBrowserEvent must originate from a pointer event
  // see https://www.w3.org/TR/pointerevents/#widl-PointerEvent-pointerType

  return pointerEvent.pointerType == 'mouse';
};
/**
 * Return `true` if the event originates from a touchable device.
 *
 * @param {import("../MapBrowserEvent.js").default} mapBrowserEvent Map browser event.
 * @return {boolean} True if the event originates from a touchable device.
 * @api
 */


exports.mouseOnly = mouseOnly;

var touchOnly = function touchOnly(mapBrowserEvent) {
  var pointerEvt =
  /** @type {import("../MapBrowserEvent").default} */
  mapBrowserEvent.originalEvent;
  (0, _asserts.assert)(pointerEvt !== undefined, 56); // mapBrowserEvent must originate from a pointer event
  // see https://www.w3.org/TR/pointerevents/#widl-PointerEvent-pointerType

  return pointerEvt.pointerType === 'touch';
};
/**
 * Return `true` if the event originates from a digital pen.
 *
 * @param {import("../MapBrowserEvent.js").default} mapBrowserEvent Map browser event.
 * @return {boolean} True if the event originates from a digital pen.
 * @api
 */


exports.touchOnly = touchOnly;

var penOnly = function penOnly(mapBrowserEvent) {
  var pointerEvt =
  /** @type {import("../MapBrowserEvent").default} */
  mapBrowserEvent.originalEvent;
  (0, _asserts.assert)(pointerEvt !== undefined, 56); // mapBrowserEvent must originate from a pointer event
  // see https://www.w3.org/TR/pointerevents/#widl-PointerEvent-pointerType

  return pointerEvt.pointerType === 'pen';
};
/**
 * Return `true` if the event originates from a primary pointer in
 * contact with the surface or if the left mouse button is pressed.
 * See https://www.w3.org/TR/pointerevents/#button-states.
 *
 * @param {import("../MapBrowserEvent.js").default} mapBrowserEvent Map browser event.
 * @return {boolean} True if the event originates from a primary pointer.
 * @api
 */


exports.penOnly = penOnly;

var primaryAction = function primaryAction(mapBrowserEvent) {
  var pointerEvent =
  /** @type {import("../MapBrowserEvent").default} */
  mapBrowserEvent.originalEvent;
  (0, _asserts.assert)(pointerEvent !== undefined, 56); // mapBrowserEvent must originate from a pointer event

  return pointerEvent.isPrimary && pointerEvent.button === 0;
};

exports.primaryAction = primaryAction;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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